/**
 * pmg-animated-hero.js  v2.0 — Z-axis tunnel effect (Gufram-style)
 *
 * Ogni prodotto vola verso la "telecamera" lungo l'asse Z.
 * Quando supera la camera, scompare e torna al fondo del tunnel.
 * Lo scroll accelera temporaneamente la velocità di avanzamento.
 *
 * – Vanilla JS puro: ZERO dipendenze esterne
 * – Completamente namespaced: nessun conflitto col tema
 * – Gestione completa Shopify Customizer
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────
     UTILS
  ───────────────────────────────────────────────── */
  const lerp  = (a, b, t) => a + (b - a) * t;
  const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
  const rand  = (lo, hi)   => lo + Math.random() * (hi - lo);

  /* ─────────────────────────────────────────────────
     CURSORE GLOBALE (montato una sola volta in <body>)
  ───────────────────────────────────────────────── */
  let sharedCursor = null;

  function getOrCreateCursor() {
    if (sharedCursor) return sharedCursor;
    const el = document.createElement('div');
    el.className = 'pmg-ah-cursor';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<div class="pmg-ah-cursor__circle"></div><div class="pmg-ah-cursor__dot"></div>';
    document.body.appendChild(el);
    sharedCursor = el;
    return el;
  }

  /* ─────────────────────────────────────────────────
     CLASSE PRINCIPALE
  ───────────────────────────────────────────────── */
  class PmgAnimatedHero {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.stage   = sectionEl.querySelector('.pmg-ah-stage');
      this.cards   = Array.from(sectionEl.querySelectorAll('.pmg-ah-card'));

      if (!this.stage || this.cards.length === 0) return;

      /* ── Config da data-* ── */
      const ds = sectionEl.dataset;

      /*
       * baseVelocity: quanto avanza ogni card verso la camera per frame
       * (range setting 1-20 → normalizziamo a 3-60 unità Z/frame)
       */
      this.baseVelocity = (parseFloat(ds.speed) || 5) * 3;

      /* Profondità del tunnel: le card partono a -DEPTH e finiscono a 0 */
      this.DEPTH      = 2000;       /* unità Z totali del tunnel */
      this.NEAR_CLIP  = 150;        /* distanza alla quale la card "passa" la camera e scompare */

      this.showCursor = ds.showCursor !== 'false';

      /* ── Stato ── */
      this.items        = [];        /* oggetti di stato per ogni card */
      this.scrollBoost  = 0;        /* velocità extra da scroll */
      this.scrollTarget = 0;        /* target del boost (lerp toward 0) */
      this.lastScrollY  = window.scrollY;
      this.raf          = null;
      this.active       = true;
      this.W = 0; this.H = 0;

      this.cursor       = this.showCursor ? getOrCreateCursor() : null;
      this.cursorActive = false;

      this._init();
    }

    /* ──────────────── SETUP ──────────────── */
    _init() {
      this._measureStage();
      this._initItems();
      this._bindEvents();
      this._setupIntersection();
      this._tick();
    }

    _measureStage() {
      const r = this.stage.getBoundingClientRect();
      this.W  = r.width;
      this.H  = r.height;
    }

    /*
     * Distribuisce le card lungo il tunnel Z con spacing uniforme,
     * così da avere sempre almeno una card "vicina" e una "lontana".
     *
     * XY: ogni card ha una posizione fissa sulla griglia XY
     * (come su Gufram: i prodotti non si muovono in X/Y, solo in Z).
     */
    _initItems() {
      const count   = this.cards.length;
      const spacing = this.DEPTH / count;   /* distanza Z tra una card e l'altra */

      this.items = [];

      this.cards.forEach((card, i) => {
        /* Posizione XY fissa: distribuita nella griglia con jitter */
        const xPct = rand(0.05, 0.95);   /* 5%-95% della larghezza */
        const yPct = rand(0.05, 0.95);   /* 5%-95% dell'altezza */

        /* Z iniziale: distribuisco le card equidistanti lungo il tunnel */
        const zInit = -(i * spacing + rand(0, spacing * 0.4));

        const item = {
          card,
          /* posizione XY come percentuale dello stage (non cambia) */
          xPct,
          yPct,
          /* posizione Z attuale */
          z: zInit,
          /* opacità calcolata */
          opacity: 0,
          /* stato hover */
          hovered: false,
        };

        this.items.push(item);

        /* Prepara lo style della card: position absolute, width auto */
        card.style.position = 'absolute';
        card.style.top      = '0';
        card.style.left     = '0';
        card.style.willChange = 'transform, opacity';
        card.style.transformOrigin = 'center center';

        this._applyTransform(item);
      });
    }

    /*
     * Proiezione prospettica manuale:
     *   – "camera" è a z=0 con un FOV dato da `perspective`
     *   – la card è a z = item.z (negativo = lontana, 0 = alla camera)
     *   – scala = perspective / (perspective - z)  (z negativo → scala < 1)
     *   – posizione sullo schermo = centro + offset * scala
     */
    _applyTransform(item) {
      const PERSPECTIVE = 800;   /* distanza focale in px */

      /* z va da -DEPTH a 0. Quando z → 0 la card è alla camera. */
      const depth = PERSPECTIVE - item.z;   /* > 0 sempre (z ≤ 0) */
      const scale = PERSPECTIVE / depth;

      /* Centro proiettivo (dove converge il "tunnel") */
      const cx = this.W  * 0.5;
      const cy = this.H  * 0.5;

      /* Posizione XY della card nello spazio 3D (coordinate di scena) */
      const sceneX = (item.xPct - 0.5) * this.W  * 1.6;
      const sceneY = (item.yPct - 0.5) * this.H  * 1.6;

      /* Proiezione: avvicina i punti al centro con la scala prospettica */
      const screenX = cx + sceneX * scale;
      const screenY = cy + sceneY * scale;

      /* Opacità: 0 quando è lontana, 1 a metà tunnel, 0 quando è vicinissima */
      const t = 1 - (-item.z / this.DEPTH);   /* 0 = fondo, 1 = camera */

      /* Fade-in dai -DEPTH.. -DEPTH*0.6, fade-out da -NEAR_CLIP..0 */
      const fadeIn  = clamp((-item.z - this.DEPTH * 0.0) / (this.DEPTH * 0.25), 0, 1);
      const fadeOut = clamp(-item.z / this.NEAR_CLIP, 0, 1);
      item.opacity  = item.hovered ? 1 : fadeIn * fadeOut;

      /* Applica su card */
      item.card.style.transform =
        `translate3d(${screenX}px, ${screenY}px, 0) ` +
        `translate(-50%, -50%) ` +
        `scale(${scale})`;
      item.card.style.opacity   = item.opacity;
      item.card.style.zIndex    = Math.round(scale * 10);
    }

    /* ──────────────── EVENTI ──────────────── */
    _bindEvents() {

      /* Scroll → boost */
      this._onWheel = (e) => {
        /* delta in px (normalizzato per pixel/line/page) */
        let delta = e.deltaY;
        if (e.deltaMode === 1) delta *= 16;   /* line */
        if (e.deltaMode === 2) delta *= 400;  /* page */
        this.scrollTarget = clamp(delta * 0.5, 0, 80);
      };
      window.addEventListener('wheel', this._onWheel, { passive: true });

      /* Scroll bar nativa */
      this._onScroll = () => {
        const sy = window.scrollY;
        const dy = sy - this.lastScrollY;
        this.lastScrollY = sy;
        const candidato = dy * 1.2;
        if (Math.abs(candidato) > Math.abs(this.scrollTarget)) {
          this.scrollTarget = clamp(candidato, 0, 80);
        }
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });

      /* Touch */
      let lastTY = null;
      this._onTouchMove = (e) => {
        const ty = e.touches[0]?.clientY;
        if (ty == null) return;
        if (lastTY !== null) {
          const dy = lastTY - ty;   /* swipe su → avanza (positivo) */
          if (Math.abs(dy * 1.5) > Math.abs(this.scrollTarget)) {
            this.scrollTarget = clamp(dy * 1.5, 0, 80);
          }
        }
        lastTY = ty;
      };
      this._onTouchEnd = () => { lastTY = null; };
      this.section.addEventListener('touchmove', this._onTouchMove, { passive: true });
      this.section.addEventListener('touchend',  this._onTouchEnd);

      /* Cursore custom */
      this._onMouseMove = (e) => {
        if (this.cursor) {
          this.cursor.style.transform = `translate3d(${e.clientX}px,${e.clientY}px,0)`;
        }
      };
      document.addEventListener('mousemove', this._onMouseMove);

      this._onMouseEnter = () => {
        if (this.cursor) this.cursor.classList.add('pmg-ah-cursor--visible');
        this.cursorActive = true;
      };
      this._onMouseLeave = () => {
        if (this.cursor) this.cursor.classList.remove('pmg-ah-cursor--visible', 'pmg-ah-cursor--hover');
        this.cursorActive = false;
      };
      this.section.addEventListener('mouseenter', this._onMouseEnter);
      this.section.addEventListener('mouseleave', this._onMouseLeave);

      this.cards.forEach((card, i) => {
        card.addEventListener('mouseenter', () => {
          this.items[i].hovered = true;
          if (this.cursor && this.cursorActive) this.cursor.classList.add('pmg-ah-cursor--hover');
        });
        card.addEventListener('mouseleave', () => {
          this.items[i].hovered = false;
          if (this.cursor) this.cursor.classList.remove('pmg-ah-cursor--hover');
        });
      });

      /* Hamburger Menu integration */
      const heroHamburger = this.section.querySelector('.js-pmg-ah-hamburger');
      if (heroHamburger) {
        this._onHamburgerClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          // Apre il drawer autonomo nella sezione hero
          const heroDrawer = document.getElementById('Drawer-Menu-Hero');
          if (heroDrawer && typeof heroDrawer.open === 'function') {
            heroDrawer.open();
          } else if (heroDrawer) {
            // Fallback: usa l'attributo open o classList
            heroDrawer.setAttribute('open', '');
            heroDrawer.classList.add('is-open');
            document.body.classList.add('overflow-hidden');
          }
        };
        heroHamburger.addEventListener('click', this._onHamburgerClick);
      }

      /* Resize */
      let rTimer;
      this._onResize = () => {
        clearTimeout(rTimer);
        rTimer = setTimeout(() => {
          this._measureStage();
          this.items.forEach(item => this._applyTransform(item));
        }, 100);
      };
      window.addEventListener('resize', this._onResize);
    }

    _setupIntersection() {
      if (!('IntersectionObserver' in window)) return;
      this._io = new IntersectionObserver(
        ([entry]) => { this.active = entry.isIntersecting; },
        { threshold: 0.01 }
      );
      this._io.observe(this.stage);
    }

    /* ──────────────── LOOP PRINCIPALE ──────────────── */
    _tick() {
      if (!this.active) {
        this.raf = requestAnimationFrame(() => this._tick());
        return;
      }

      /* Smooth del boost verso target, poi decadimento del target verso 0 */
      this.scrollBoost  = lerp(this.scrollBoost,  this.scrollTarget, 0.12);
      this.scrollTarget = lerp(this.scrollTarget, 0,                 0.09);
      if (Math.abs(this.scrollBoost)  < 0.05) this.scrollBoost  = 0;
      if (Math.abs(this.scrollTarget) < 0.05) this.scrollTarget = 0;

      /* Velocità frame: base + boost da scroll */
      const velocity = this.baseVelocity + this.scrollBoost;

      this.items.forEach((item) => {
        if (item.hovered) {
          /* Hover: blocca l'avanzamento Z */
          this._applyTransform(item);
          return;
        }

        /* Avanza verso la camera (z aumenta verso 0) */
        item.z += velocity;

        /* Quando supera la NEAR_CLIP (è passata davanti alla camera):
           → la card torna in fondo al tunnel, a una nuova posizione XY casuale */
        if (item.z >= -this.NEAR_CLIP * 0.5) {
          item.z    = -this.DEPTH + rand(-this.DEPTH * 0.1, 0);
          item.xPct = rand(0.05, 0.95);
          item.yPct = rand(0.05, 0.95);
          item.opacity = 0;
        }



        this._applyTransform(item);
      });

      this.raf = requestAnimationFrame(() => this._tick());
    }

    /* ──────────────── DESTROY ──────────────── */
    destroy() {
      cancelAnimationFrame(this.raf);
      window.removeEventListener('wheel',    this._onWheel);
      window.removeEventListener('scroll',   this._onScroll);
      window.removeEventListener('resize',   this._onResize);
      document.removeEventListener('mousemove', this._onMouseMove);
      this.section.removeEventListener('mouseenter', this._onMouseEnter);
      this.section.removeEventListener('mouseleave', this._onMouseLeave);
      this.section.removeEventListener('touchmove', this._onTouchMove);
      this.section.removeEventListener('touchend',  this._onTouchEnd);
      if (this._onHamburgerClick) {
        const heroHamburger = this.section.querySelector('.js-pmg-ah-hamburger');
        if (heroHamburger) heroHamburger.removeEventListener('click', this._onHamburgerClick);
      }
      if (this._io) this._io.disconnect();
      if (this.cursor) this.cursor.classList.remove('pmg-ah-cursor--visible');
    }

    refresh() {
      cancelAnimationFrame(this.raf);
      this.items = [];
      const ds = this.section.dataset;
      this.baseVelocity = (parseFloat(ds.speed) || 5) * 3;
      this._measureStage();
      this._initItems();
      this._tick();
    }
  }

  /* ─────────────────────────────────────────────────
     GESTIONE ISTANZE
  ───────────────────────────────────────────────── */
  const registry = new Map();

  function boot(root) {
    if (!root) return;
    const el = root.querySelector?.('.pmg-ah-section') ?? root;
    if (!el.classList.contains('pmg-ah-section')) return;
    if (registry.has(el)) return;
    registry.set(el, new PmgAnimatedHero(el));
  }

  function teardown(root) {
    if (!root) return;
    const el = root.querySelector?.('.pmg-ah-section') ?? root;
    const inst = registry.get(el);
    if (inst) { inst.destroy(); registry.delete(el); }
  }

  /* Init */
  const run = () => document.querySelectorAll('.pmg-ah-section').forEach(el => boot(el));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  /* Shopify Customizer */
  document.addEventListener('shopify:section:load',   e => boot(e.target));
  document.addEventListener('shopify:section:unload', e => teardown(e.target));
  document.addEventListener('shopify:section:select', e => {
    const el = e.target.querySelector('.pmg-ah-section') ?? e.target;
    registry.get(el)?._measureStage();
  });

})();
